import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import eOpsLogo from '../../images/eops.png';
import eOpsLogoDark from '../../images/eops-white-01.png';
import pitcLogo from '../../images/pitc.png';
import './LandingLogoIntro.scss';

const resolveIntroEopsLogo = () => {
  if (typeof document === 'undefined') return eOpsLogo;
  try {
    if (document.documentElement.getAttribute('data-sidebar-theme') === 'dark') {
      return eOpsLogoDark;
    }
    if (localStorage.getItem('eops-sidebar-theme') === 'dark') {
      return eOpsLogoDark;
    }
  } catch {
    // ignore
  }
  return eOpsLogo;
};

export const LANDING_LOGO_INTRO_KEY = 'eopsLandingLogoIntro';
const INTRO_COVER_ID = 'eops-landing-intro-cover';
const INTRO_BODY_CLASS = 'eops-landing-intro-active';

export const markLandingLogoIntro = () => {
  try {
    sessionStorage.setItem(LANDING_LOGO_INTRO_KEY, '1');
  } catch (e) {
    // ignore
  }
};

export const shouldPlayLandingLogoIntro = () => {
  try {
    return sessionStorage.getItem(LANDING_LOGO_INTRO_KEY) === '1';
  } catch (e) {
    return false;
  }
};

export const clearLandingLogoIntro = () => {
  try {
    sessionStorage.removeItem(LANDING_LOGO_INTRO_KEY);
  } catch (e) {
    // ignore
  }
};

/** Soft cover before route swap — keeps login→landing handoff clean */
export const mountLandingIntroCover = () => {
  if (typeof document === 'undefined') return;
  document.body.classList.add(INTRO_BODY_CLASS);
  if (document.getElementById(INTRO_COVER_ID)) return;
  const el = document.createElement('div');
  el.id = INTRO_COVER_ID;
  el.setAttribute('aria-hidden', 'true');
  el.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:99999',
    'background:rgba(15,40,72,0.14)',
    'backdrop-filter:blur(10px)',
    '-webkit-backdrop-filter:blur(10px)',
    'pointer-events:none',
  ].join(';');
  document.body.appendChild(el);
};

export const removeLandingIntroCover = () => {
  if (typeof document === 'undefined') return;
  document.getElementById(INTRO_COVER_ID)?.remove();
};

const hideSidebarLogo = (el) => {
  if (!el) return;
  el.classList.add('eops-sidebar-logo--waiting');
  el.classList.remove('eops-sidebar-logo--arrived');
};

const revealSidebarLogo = (el) => {
  if (!el) return;
  el.classList.remove('eops-sidebar-logo--waiting');
  el.classList.add('eops-sidebar-logo--arrived');
  el.style.opacity = '1';
  el.style.visibility = 'visible';
};

const isReadyTarget = (el) => {
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.width >= 40 && rect.height >= 12;
};

const FALLBACK_TARGET = { left: 22, top: 16, width: 118, height: 42 };
const FLY_MS = 1500;
/** Full 180° card flip — both faces always mounted (no blink) */
const FLIP_MS = 680;
const FLIP_HOLD_MS = 420;

/**
 * Post-login: eOps logo flies to sidebar.
 * On hit: profile-style spin — eOps → PITC → eOps.
 */
const LandingLogoIntro = ({ onComplete }) => {
  const [phase, setPhase] = useState('enter'); // enter | fly | exit | done
  const [glassOff, setGlassOff] = useState(false); // clear frosted overlay before flips
  const [flipDeg, setFlipDeg] = useState(0);
  // Freeze logo at mount so dark/light does not swap mid-flight.
  const [introLogo] = useState(() => resolveIntroEopsLogo());
  const flyerRef = useRef(null);
  const faceRef = useRef(null);
  const completedRef = useRef(false);
  const targetRef = useRef(null);
  const timersRef = useRef([]);
  const landTransformRef = useRef('');

  const clearTimers = () => {
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current = [];
  };

  const later = (fn, ms) => {
    const id = setTimeout(fn, ms);
    timersRef.current.push(id);
    return id;
  };

  useEffect(() => {
    removeLandingIntroCover();
    document.body.classList.add(INTRO_BODY_CLASS);

    // Preload both faces so flip never flashes empty
    [introLogo, eOpsLogo, eOpsLogoDark, pitcLogo].forEach((src) => {
      const img = new Image();
      img.src = src;
    });

    const hideExisting = () => {
      const target = document.getElementById('eops-sidebar-logo');
      if (target) {
        hideSidebarLogo(target);
        targetRef.current = target;
        return true;
      }
      return false;
    };
    hideExisting();

    const pollId = window.setInterval(hideExisting, 40);
    const holdTimer = setTimeout(() => {
      clearInterval(pollId);
      setPhase('fly');
    }, 1200);

    return () => {
      clearTimeout(holdTimer);
      clearInterval(pollId);
      clearTimers();
      document.body.classList.remove(INTRO_BODY_CLASS);
    };
  }, []);

  useEffect(() => {
    if (phase !== 'fly' || !flyerRef.current) return undefined;

    const flyerEl = flyerRef.current;
    let pollTimer;
    let attempts = 0;
    let cancelled = false;

    const beginExit = () => {
      if (completedRef.current) return;
      completedRef.current = true;
      clearLandingLogoIntro();
      setPhase('exit');
    };

    /** Continuous Y flip — 0° eOps, 180° PITC, 360° eOps */
    const flipTo = (deg, then) => {
      const flipEl = faceRef.current;
      if (flipEl) {
        flipEl.style.transition = `transform ${FLIP_MS}ms linear`;
      }
      setFlipDeg(deg);
      later(() => {
        if (cancelled) return;
        then?.();
      }, FLIP_MS);
    };

    const runFly = (target) => {
      if (cancelled) return;
      targetRef.current = target;
      if (target) hideSidebarLogo(target);

      const from = flyerEl.getBoundingClientRect();
      const toRect = target && isReadyTarget(target)
        ? target.getBoundingClientRect()
        : FALLBACK_TARGET;

      const toWidth = Math.max(toRect.width || FALLBACK_TARGET.width, 80);
      const dx = toRect.left + toRect.width / 2 - (from.left + from.width / 2);
      const dy = toRect.top + toRect.height / 2 - (from.top + from.height / 2);
      const scale = toWidth / Math.max(from.width, 1);

      setFlipDeg(0);
      flyerEl.classList.add('landing-logo-intro__flyer--flying');
      flyerEl.style.transition = `transform ${FLY_MS}ms cubic-bezier(0.33, 0.1, 0.2, 1)`;
      flyerEl.style.opacity = '1';
      void flyerEl.offsetWidth;
      const landTf = `translate(${dx}px, ${dy}px) scale(${scale})`;
      landTransformRef.current = landTf;
      flyerEl.style.transform = landTf;

      // Hit sidebar: drop glass first, then flip over clear UI
      later(() => {
        if (cancelled) return;
        setGlassOff(true);
        flipTo(180, () => {
          later(() => {
            if (cancelled) return;
            flipTo(360, () => {
              later(() => {
                if (cancelled) return;
                revealSidebarLogo(target);
                flyerEl.style.transition = 'opacity 0.12s linear';
                flyerEl.style.opacity = '0';
                later(() => beginExit(), 120);
              }, 80);
            });
          }, FLIP_HOLD_MS);
        });
      }, FLY_MS);
    };

    const tryFly = () => {
      if (cancelled) return;
      const target = document.getElementById('eops-sidebar-logo');
      if (target && isReadyTarget(target)) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => runFly(target));
        });
        return;
      }
      if (attempts >= 40) {
        runFly(target || null);
        return;
      }
      attempts += 1;
      pollTimer = setTimeout(tryFly, 50);
    };

    const startId = requestAnimationFrame(() => {
      requestAnimationFrame(tryFly);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(startId);
      if (pollTimer) clearTimeout(pollTimer);
      clearTimers();
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== 'exit') return undefined;

    const doneTimer = setTimeout(() => {
      setPhase('done');
      onComplete?.();
    }, 500);

    return () => clearTimeout(doneTimer);
  }, [phase, onComplete]);

  if (phase === 'done' || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={[
        'landing-logo-intro',
        `landing-logo-intro--${phase}`,
        glassOff ? 'landing-logo-intro--glass-off' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label="Welcome to eOps"
      role="status"
    >
      <div ref={flyerRef} className="landing-logo-intro__flyer">
        <div
          ref={faceRef}
          className="landing-logo-intro__flipper"
          style={{ transform: `rotateY(${flipDeg}deg)` }}
        >
          <img
            src={introLogo}
            alt="eOps"
            className="landing-logo-intro__face landing-logo-intro__face--front"
            draggable={false}
          />
          <img
            src={pitcLogo}
            alt="PITC"
            className="landing-logo-intro__face landing-logo-intro__face--back"
            draggable={false}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LandingLogoIntro;
